import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { MediaFile } from "@models/Common";
import { MediaType } from "@models/Media";
import { MAX_MEMORIAL_DURATION_LENGTH_SECONDS } from "@constants/validation";
import styles from "./MediaSelector.module.css";

interface MediaSelectorProps {
    label?: string;
    id: string;
    maxSize?: number;
    maxSizeImage?: number;
    maxSizeVideo?: number;
    maxVideoLength?: number;
    media: MediaFile | null;
    errorMessage?: string;
    onMediaChange: (
        media: MediaFile | MediaFile[] | null,
        error: string
    ) => void;
    mediaTypeFilter?: "image" | "video" | "both";
    hideChooseButtonOnSelect?: boolean;
    multiple?: boolean; // optional, default false
}

const MediaSelector: FC<MediaSelectorProps> = ({
    label,
    id,
    maxSize = 25,
    maxSizeImage,
    maxSizeVideo,
    maxVideoLength = MAX_MEMORIAL_DURATION_LENGTH_SECONDS,
    media,
    errorMessage,
    onMediaChange,
    mediaTypeFilter = "both",
    hideChooseButtonOnSelect = false,
    multiple = false,
}) => {
    const [mediaType, setMediaType] = useState<MediaType | null>(null);

    useEffect(() => {
        if (!media || typeof media !== "string") {
            setMediaType(null);
            return;
        }

        if (media.startsWith("data:image")) {
            setMediaType("image");
        } else if (media.startsWith("data:video")) {
            setMediaType("video");
        } else {
            const mediaUrl = media.split("?")[0];
            const isImage = /\.(jpeg|jpg|gif|png|webp|bmp|svg|tiff|ico|avif)$/i.test(mediaUrl);
            const isVideo = /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v|3gp|f4v|mts)$/i.test(mediaUrl);
            setMediaType(isImage ? "image" : isVideo ? "video" : null);
        }
    }, [media]);

    const handleMediaChange = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const processedFiles: MediaFile[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            const fileType = file.type.startsWith("image/")
                ? "image"
                : file.type.startsWith("video/")
                ? "video"
                : null;

            if (!fileType) {
                onMediaChange(null, "Unsupported file type.");
                return;
            }

            // restore mediaType for single preview
            if (!multiple) {
                setMediaType(fileType);
            }

            const applicableMaxSize =
                fileType === "image"
                    ? maxSizeImage || maxSize
                    : maxSizeVideo || maxSize;

            if (file.size > applicableMaxSize * 1024 * 1024) {
                onMediaChange(null, `File size should be less than ${applicableMaxSize}MB`);
                return;
            }

            const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(file);
            });

            processedFiles.push(base64);
        }

        if (multiple) {
            onMediaChange(processedFiles, "");
        } else {
            onMediaChange(processedFiles[0], "");
        }
    };

    const getAcceptedMediaTypes = () => {
        if (mediaTypeFilter === "image") return "image/*";
        if (mediaTypeFilter === "video") return "video/*";
        return "image/*,video/*";
    };

    return (
        <div className={styles.container}>
            {label && (
                <label htmlFor={id} className={styles.label}>
                    {label}
                </label>
            )}

            {(!hideChooseButtonOnSelect ||
                (hideChooseButtonOnSelect && !media)) && (
                <input
                    type="file"
                    accept={getAcceptedMediaTypes()}
                    multiple={multiple}
                    onChange={handleMediaChange}
                    id={id}
                    className={`${styles.input} ${errorMessage ? styles.invalid : ""}`}
                />
            )}

            {errorMessage && (
                <div className={styles.errorMessage}>{errorMessage}</div>
            )}

            {/* Preview only in single mode */}
            {!multiple && media && mediaType === "image" && (
                <img
                    src={media as string}
                    alt="Selected media preview"
                    className={styles.preview}
                />
            )}

            {!multiple && media && mediaType === "video" && (
                <video controls className={styles.preview}>
                    <source src={media as string} />
                </video>
            )}
        </div>
    );
};

export default MediaSelector;